import { neo4jDriver } from '../neo4jDriver';
import { DEFAULT_FREE_SHIPPING_CARD, normalizeFreeShippingCard } from '../config/freeShippingCard';

const database = process.env.REACT_APP_NEO4J_DATABASE;

// Use the shared driver instance
const driver = neo4jDriver;

// Customer authentication functions
export const loginCustomer = async (email, password) => {
  const session = driver.session({ database });
  try {
    const result = await session.executeRead(tx =>
      tx.run(
        `
        MATCH (c:Customer {email: $email, password: $password})
        RETURN c
        `,
        { email, password }
      )
    );

    if (result.records.length === 0) {
      return { success: false, message: 'Invalid email or password' };
    }

    const customer = result.records[0].get('c').properties;
    return { success: true, customer };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, message: 'An error occurred during login' };
  } finally {
    await session.close();
  }
};

export const registerCustomer = async (customerData) => {
  const session = driver.session({ database });
  try {
    // Check if email already exists in either Customer or User nodes
    const existingUser = await session.executeRead(tx =>
      tx.run(
        `
        MATCH (n)
        WHERE (n:Customer OR n:User) AND n.email = $email
        RETURN n
        `,
        { email: customerData.email }
      )
    );

    if (existingUser.records.length > 0) {
      return { success: false, message: 'This email address is already registered. Please use a different email or login to your existing account.' };
    }

    // Create new customer
    const result = await session.executeWrite(tx =>
      tx.run(
        `
        CREATE (c:Customer {
          id: randomUUID(),
          name: $name,
          email: $email,
          password: $password,
          role: "customer",
          createdAt: datetime()
        })
        RETURN c { .* } as customer
        `,
        {
          name: customerData.name,
          email: customerData.email,
          password: customerData.password
        }
      )
    );

    if (!result.records[0]) {
      return { success: false, message: 'Failed to create customer record' };
    }

    const customer = result.records[0].get('customer');
    return { success: true, customer: { ...customer, role: 'customer' } };
  } catch (error) {
    console.error('Registration error:', error);
    return { success: false, message: 'An error occurred during registration' };
  } finally {
    await session.close();
  }
};

export const neo4jService = {
  getFreeShippingCard: async () => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(
          `
          MATCH (s:SiteSetting {key: "freeShippingCard"})
          RETURN s
          `
        )
      );

      if (result.records.length === 0) {
        return DEFAULT_FREE_SHIPPING_CARD;
      }

      return normalizeFreeShippingCard(result.records[0].get('s').properties);
    } finally {
      await session.close();
    }
  },

  updateFreeShippingCard: async (settings) => {
    const nextSettings = normalizeFreeShippingCard(settings);
    const session = driver.session({ database });
    try {
      const result = await session.executeWrite(tx =>
        tx.run(
          `
          MERGE (s:SiteSetting {key: "freeShippingCard"})
          SET s.enabled = $enabled,
              s.title = $title,
              s.homeDescription = $homeDescription,
              s.color = $color,
              s.updatedAt = datetime()
          RETURN s
          `,
          nextSettings
        )
      );

      return normalizeFreeShippingCard(result.records[0].get('s').properties);
    } finally {
      await session.close();
    }
  },

  // Create a product
  createProduct: async (product) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeWrite(tx => 
        tx.run(
          `
          CREATE (p:Product {
            id: $id,
            name: $name,
            price: $price,
            originalPrice: $originalPrice,
            category: $category,
            brand: $brand,
            condition: $condition,
            description: $description,
            shippingTitle: $shippingTitle,
            shippingDescription: $shippingDescription,
            shippingCost: $shippingCost,
            deliveryTitle: $deliveryTitle,
            deliveryDescription: $deliveryDescription,
            deliveryCost: $deliveryCost,
            warrantyTitle: $warrantyTitle,
            warrantyDescription: $warrantyDescription,
            returnTitle: $returnTitle,
            returnDescription: $returnDescription,
            rating: $rating,
            reviewsCount: $reviewsCount,
            availability: $availability,
            inStock: $inStock,
            qty_instock: $qty_instock,
            model: $model,
            sku: $sku,
            main_image: $main_image,
            tag: $tag,
            isNew: $isNew,
            isFeatured: $isFeatured,
            features: $features,
            images: $images,
            createdAt: datetime()
          })
          RETURN p
          `,
          {
            ...product,
            features: product.features || [],
            images: product.images || [],
            condition: product.condition || 'New',
            shippingTitle: product.shippingTitle || '',
            shippingDescription: product.shippingDescription || '',
            shippingCost: product.shippingCost || '',
            deliveryTitle: product.deliveryTitle || '',
            deliveryDescription: product.deliveryDescription || '',
            deliveryCost: product.deliveryCost || '',
            warrantyTitle: product.warrantyTitle || '',
            warrantyDescription: product.warrantyDescription || '',
            returnTitle: product.returnTitle || '',
            returnDescription: product.returnDescription || '',
            isFeatured: product.isFeatured || false,
            id: product.id || Date.now().toString()
          }
        )
      );
      return result.records[0].get('p').properties;
    } finally {
      await session.close();
    }
  },

  // Get all products
  getAllProducts: async () => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(`
          MATCH (p:Product)
          OPTIONAL MATCH (c:Customer)-[r:RATED]->(p)
          WITH p, count(r) as reviewCount, avg(r.rating) as avgRating
          RETURN p, reviewCount, avgRating
        `)
      );
      return result.records.map(record => {
        const props = record.get('p').properties;
        const reviewCount = record.get('reviewCount');
        const avgRating = record.get('avgRating');
        return {
          ...props,
          reviewsCount: typeof reviewCount.toNumber === 'function' ? 
            reviewCount.toNumber() : Number(reviewCount),
          rating: avgRating ? Number(avgRating) : 0
        };
      });
    } finally {
      await session.close();
    }
  },

  // Get featured products
  getFeaturedProducts: async () => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(`
          MATCH (p:Product)
          WHERE p.isFeatured = true
          OPTIONAL MATCH (c:Customer)-[r:RATED]->(p)
          WITH p, count(r) as reviewCount, avg(r.rating) as avgRating
          RETURN p, reviewCount, avgRating
        `)
      );
      return result.records.map(record => {
        const props = record.get('p').properties;
        const reviewCount = record.get('reviewCount');
        const avgRating = record.get('avgRating');
        return {
          ...props,
          reviewsCount: typeof reviewCount.toNumber === 'function' ? 
            reviewCount.toNumber() : Number(reviewCount),
          rating: avgRating ? Number(avgRating) : 0
        };
      });
    } finally {
      await session.close();
    }
  },

  // Get products by category
  getProductsByCategory: async (category) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(
          `
          MATCH (p:Product) 
          WHERE toLower(p.category) = toLower($category) 
          OR toLower(p.subCategory) = toLower($category)
          RETURN p {
            .id,
            .name,
            .brand,
            .condition,
            .price,
            .originalPrice,
            .category,
            .subCategory,
            .description,
            .main_image,
            .model,
            .qty_instock,
            rating: toFloat(p.rating),
            reviewsCount: toInteger(p.reviewsCount)
          } as product
          `,
          { category }
        )
      );
      return result.records.map(record => {
        const product = record.get('product');
        return {
          ...product,
          rating: parseFloat(product.rating) || 0,
          reviewsCount: parseInt(product.reviewsCount) || 0
        };
      });
    } finally {
      await session.close();
    }
  },

  getAvailableCategories: async () => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(`
          MATCH (p:Product)
          UNWIND [p.category, p.subCategory] AS categoryName
          WITH trim(categoryName) AS categoryName
          WHERE categoryName IS NOT NULL AND categoryName <> ''
          RETURN DISTINCT categoryName
          ORDER BY categoryName
        `)
      );

      return result.records.map(record => record.get('categoryName'));
    } finally {
      await session.close();
    }
  },

  // Update a product
  updateProduct: async (id, updates) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeWrite(tx =>
        tx.run(
          `
          MATCH (p:Product {id: $id})
          SET p += $updates
          RETURN p
          `,
          { id, updates }
        )
      );
      return result.records[0].get('p').properties;
    } finally {
      await session.close();
    }
  },

  // Toggle featured status
  toggleFeatured: async (id) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeWrite(tx =>
        tx.run(
          `
          MATCH (p:Product {id: $id})
          SET p.isFeatured = NOT p.isFeatured
          RETURN p
          `,
          { id }
        )
      );
      return result.records[0].get('p').properties;
    } finally {
      await session.close();
    }
  },

  // Delete a product
  deleteProduct: async (id) => {
    const session = driver.session({ database });
    try {
      await session.executeWrite(tx =>
        tx.run(
          'MATCH (p:Product {id: $id}) DELETE p',
          { id }
        )
      );
      return true;
    } finally {
      await session.close();
    }
  },

  // Initialize database with sample products
  initializeProducts: async (products) => {
    const session = driver.session({ database });
    try {
      // First, check if products already exist
      const existingProducts = await session.executeRead(tx =>
        tx.run('MATCH (p:Product) RETURN count(p) as count')
      );
      
      if (existingProducts.records[0].get('count').low === 0) {
        // Only initialize if no products exist
        await session.executeWrite(tx =>
          tx.run(
            `
            UNWIND $products as product
            CREATE (p:Product)
            SET p = product,
                p.id = toString(id(p)),
                p.createdAt = datetime()
            RETURN p
            `,
            { products }
          )
        );
      }
    } finally {
      await session.close();
    }
  },

  // Add or update rating for a product
  addRating: async (productId, userEmail, rating) => {
    const session = driver.session({ database });
    try {
      // First verify that both product and customer exist
      const verifyResult = await session.executeRead(tx => {
        return tx.run(
          `
          MATCH (p:Product)
          WHERE p.id = $productId
          WITH p
          MATCH (c:Customer)
          WHERE c.email = $userEmail
          RETURN p, c
          `,
          { productId, userEmail }
        );
      });

      if (!verifyResult.records.length) {
        throw new Error('Product or customer not found');
      }

      // Now add or update the rating
      const result = await session.executeWrite(tx => {
        return tx.run(
          `
          MATCH (p:Product)
          WHERE p.id = $productId
          MATCH (c:Customer)
          WHERE c.email = $userEmail
          MERGE (c)-[r:RATED]->(p)
          ON CREATE SET r.rating = toFloat($rating), r.timestamp = datetime()
          ON MATCH SET r.rating = toFloat($rating), r.timestamp = datetime()
          WITH p
          OPTIONAL MATCH (c2:Customer)-[r2:RATED]->(p)
          WITH p, CASE WHEN count(r2) > 0 THEN avg(r2.rating) ELSE 0 END as avgRating, count(r2) as reviewCount
          SET p.rating = avgRating,
              p.reviewsCount = reviewCount
          RETURN toFloat(p.rating) as rating, toInteger(p.reviewsCount) as reviewCount
          `,
          { productId, userEmail, rating }
        );
      });

      if (!result.records.length) {
        throw new Error('Failed to update rating');
      }

      const record = result.records[0];
      return {
        rating: parseFloat(record.get('rating')),
        reviewsCount: parseInt(record.get('reviewCount'))
      };
    } catch (error) {
      console.error('Error in addRating:', error);
      throw error;
    } finally {
      await session.close();
    }
  },

  // Get user rating for a product
  getUserRating: async (productId, userEmail) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx => {
        return tx.run(
          `
          MATCH (c:Customer)
          WHERE c.email = $userEmail
          MATCH (p:Product)
          WHERE p.id = $productId
          MATCH (c)-[r:RATED]->(p)
          RETURN r.rating as rating
          `,
          { productId, userEmail }
        );
      });
      const rating = result.records.length > 0 ? result.records[0].get('rating') : null;
      return rating ? rating.toNumber() : null;
    } catch (error) {
      console.error('Error in getUserRating:', error);
      return null;
    } finally {
      await session.close();
    }
  },

  // Register a new customer
  registerCustomer: async (email, password, name) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeWrite(tx =>
        tx.run(
          `
          MATCH (c:Customer {email: $email}) 
          RETURN c
          `,
          { email }
        )
      );

      if (result.records.length > 0) {
        throw new Error('Email already registered');
      }

      const createResult = await session.executeWrite(tx =>
        tx.run(
          `
          CREATE (c:Customer {
            id: $id,
            email: $email,
            password: $password,
            name: $name,
            role: "customer",
            createdAt: datetime()
          })
          RETURN c
          `,
          {
            id: Date.now().toString(),
            email,
            password,
            name
          }
        )
      );

      const newCustomer = createResult.records[0].get('c').properties;
      delete newCustomer.password;
      return { ...newCustomer, role: 'customer' };
    } finally {
      await session.close();
    }
  },

  // Login customer
  loginCustomer: async (email, password) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(
          `
          MATCH (c:Customer {email: $email, password: $password})
          RETURN c
          `,
          { email, password }
        )
      );

      if (result.records.length === 0) {
        throw new Error('Invalid email or password');
      }

      const customer = result.records[0].get('c').properties;
      delete customer.password;
      return customer;
    } finally {
      await session.close();
    }
  },

  async updateCustomerProfile(customerId, updates) {
    const session = driver.session({ database });
    try {
      const updateString = Object.keys(updates)
        .filter(key => key !== 'id' && key !== 'password')
        .map(key => `c.${key} = $${key}`)
        .join(', ');

      const result = await session.executeWrite(tx => {
        return tx.run(
          `
          MATCH (c:Customer {id: $customerId})
          SET ${updateString}, c.updatedAt = datetime()
          RETURN c
          `,
          { customerId, ...updates }
        );
      });

      if (result.records.length === 0) {
        throw new Error('Customer not found');
      }

      const customer = result.records[0].get('c').properties;
      delete customer.password;
      return customer;
    } finally {
      await session.close();
    }
  },

  async getCustomerProfile(customerId) {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(
          `
          MATCH (c:Customer {id: $customerId})
          OPTIONAL MATCH (c)-[r:RATED]->(p:Product)
          WITH c, collect({
            rating: r.rating,
            timestamp: r.timestamp,
            productId: p.id,
            productName: p.name
          }) as ratings
          RETURN {
            id: c.id,
            name: c.name,
            email: c.email,
            createdAt: c.createdAt,
            ratings: ratings
          } as profile
          `,
          { customerId }
        )
      );

      if (result.records.length === 0) {
        return null;
      }

      return result.records[0].get('profile');
    } catch (error) {
      console.error('Error fetching customer profile:', error);
      throw new Error('Failed to fetch customer profile');
    } finally {
      await session.close();
    }
  },

  getProductById: async (productId) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(
          `
          MATCH (p:Product)
          WHERE p.id = $productId
          OPTIONAL MATCH (c:Customer)-[r:RATED]->(p)
          WITH p, count(r) as reviewCount, avg(r.rating) as avgRating
          RETURN p, reviewCount, avgRating
          `,
          { productId }
        )
      );
      
      if (result.records.length === 0) {
        return null;
      }
      
      const record = result.records[0];
      const props = record.get('p').properties;
      const reviewCount = record.get('reviewCount');
      const avgRating = record.get('avgRating');

      const product = {
        ...props,
        reviewsCount: typeof reviewCount.toNumber === 'function' ? 
          reviewCount.toNumber() : Number(reviewCount),
        rating: avgRating ? Number(avgRating) : 0
      };
      return product;
    } catch (error) {
      throw error;
    } finally {
      await session.close();
    }
  },

  searchProducts: async (searchQuery) => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(
          `
          MATCH (p:Product)
          WHERE toLower(p.name) CONTAINS toLower($searchQuery)
          OR toLower(p.description) CONTAINS toLower($searchQuery)
          OR toLower(p.brand) CONTAINS toLower($searchQuery)
          OPTIONAL MATCH (c:Customer)-[r:RATED]->(p)
          WITH p, count(r) as reviewCount, avg(r.rating) as avgRating
          RETURN p, reviewCount, avgRating
          `,
          { searchQuery }
        )
      );
      
      return result.records.map(record => {
        const props = record.get('p').properties;
        const reviewCount = record.get('reviewCount');
        const avgRating = record.get('avgRating');
        return {
          ...props,
          reviewsCount: typeof reviewCount.toNumber === 'function' ? 
            reviewCount.toNumber() : Number(reviewCount),
          rating: avgRating ? Number(avgRating) : 0
        };
      });
    } finally {
      await session.close();
    }
  },

  getHeroSlides: async () => {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(`
          MATCH (s:HeroSlide)
          RETURN s
          ORDER BY s.order
        `)
      );
      return result.records.map(record => record.get('s').properties);
    } finally {
      await session.close();
    }
  },

  addHeroSlide: async (slideData) => {
    const session = driver.session({ database });
    try {
      const addResult = await session.executeWrite(tx =>
        tx.run(
          `
              CREATE (s:HeroSlide {
                id: $id,
                title: $title,
                subtitle: $subtitle,
                description: $description,
                category: $category,
                buttonText: $buttonText,
                productId: $productId,
                image: $image,
                color: $color,
                order: $order
              })
            RETURN s
            `,
            slideData
        )
      );
      return addResult.records[0].get('s').properties;
    } finally {
      await session.close();
    }
  },

  updateHeroSlide: async (slideId, slideData) => {
    const session = driver.session({ database });
    try {
      await session.executeWrite(tx =>
        tx.run(
          `
            MATCH (s:HeroSlide {id: $slideId})
              SET s.title = $title,
                  s.subtitle = $subtitle,
                  s.description = $description,
                  s.category = $category,
                  s.buttonText = $buttonText,
                  s.productId = $productId,
                  s.image = $image,
                  s.color = $color,
                  s.order = $order
              `,
              { slideId, ...slideData }
            )
        );
      } finally {
        await session.close();
      }
    },

  updateHeroSlideOrder: async (slides) => {
      const session = driver.session({ database });
      try {
        await session.executeWrite(tx =>
          tx.run(
            `
            UNWIND $slides as slide
            MATCH (s:HeroSlide {id: slide.id})
            SET s.order = slide.order
            `,
            {
              slides: slides.map((slide, index) => ({
                id: slide.id,
                order: index
              }))
            }
          )
        );
      } finally {
        await session.close();
      }
    },

  deleteHeroSlide: async (slideId) => {
    const session = driver.session({ database });
    try {
      await session.executeWrite(tx =>
        tx.run(
          `
          MATCH (s:HeroSlide {id: $slideId})
          DELETE s
          `,
          { slideId }
        )
      );
    } finally {
      await session.close();
    }
  },

  async addToWishlist(userId, productId) {
    const session = driver.session({ database });
    try {
      const result = await session.run(
        `
        MATCH (u:Customer {id: $userId})
        MATCH (p:Product {id: $productId})
        MERGE (u)-[r:WISHLISTED]->(p)
        RETURN p
        `,
        { userId, productId }
      );
      return result.records.length > 0;
    } finally {
      await session.close();
    }
  },

  async removeFromWishlist(userId, productId) {
    const session = driver.session({ database });
    try {
      const result = await session.run(
        `
        MATCH (u:Customer {id: $userId})-[r:WISHLISTED]->(p:Product {id: $productId})
        DELETE r
        RETURN p
        `,
        { userId, productId }
      );
      return result.records.length > 0;
    } finally {
      await session.close();
    }
  },

  async getWishlist(userId) {
    const session = driver.session({ database });
    try {
      const result = await session.run(
        `
        MATCH (u:Customer {id: $userId})-[:WISHLISTED]->(p:Product)
        RETURN p
        `,
        { userId }
      );
      return result.records.map(record => {
        const product = record.get('p').properties;
        return {
          ...product,
          price: parseFloat(product.price),
          originalPrice: product.originalPrice ? parseFloat(product.originalPrice) : null,
          qty_instock: parseInt(product.qty_instock)
        };
      });
    } finally {
      await session.close();
    }
  },

  async isProductWishlisted(userId, productId) {
    const session = driver.session({ database });
    try {
      const result = await session.run(
        `
        MATCH (u:Customer {id: $userId})-[r:WISHLISTED]->(p:Product {id: $productId})
        RETURN r
        `,
        { userId, productId }
      );
      return result.records.length > 0;
    } finally {
      await session.close();
    }
  },

  async getCustomerOrders(customerId) {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(
          `
          MATCH (c:Customer)-[o:ORDERED]->(p:Product)
          WHERE toString(c.id) = toString($customerId)
             OR toLower(c.email) = toLower($customerId)
          RETURN {
            orderId: o.orderId,
            productId: p.id,
            productName: p.name,
            productImage: coalesce(p.main_image, p.image, ""),
            price: p.price,
            quantity: o.quantity,
            status: o.status,
            orderDate: o.orderDate,
            shippingAddress: o.shippingAddress,
            fulfillmentType: o.fulfillmentType,
            pickupName: o.pickupName,
            pickupPhone: o.pickupPhone,
            pickupEmail: o.pickupEmail,
            pickupDate: o.pickupDate,
            pickupTime: o.pickupTime,
            pickupScheduledAt: o.pickupScheduledAt,
            pickupStatus: o.pickupStatus,
            pickupNotes: o.pickupNotes,
            trackingNumber: o.trackingNumber
          } as order
          ORDER BY o.orderDate DESC
          `,
          { customerId }
        )
      );

      return result.records.map(record => record.get('order'));
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      throw new Error('Failed to fetch customer orders');
    } finally {
      await session.close();
    }
  },

  async createPickupOrder(customerId, cartItems, pickupDetails = {}) {
    const session = driver.session({ database });
    const orderId = `PU-${Date.now().toString(36).toUpperCase()}`;
    const orderDate = new Date().toISOString();
    const pickupDate = pickupDetails.date || '';
    const pickupTime = pickupDetails.time || '';
    const pickupScheduledAt = pickupDate && pickupTime ? `${pickupDate}T${pickupTime}` : '';

    try {
      const result = await session.executeWrite(tx =>
        tx.run(
          `
          MATCH (c:Customer)
          WHERE toString(c.id) = toString($customerId)
             OR toLower(c.email) = toLower($customerId)
          UNWIND $items as item
          MATCH (p:Product)
          WHERE toString(p.id) = toString(item.id)
          CREATE (c)-[:ORDERED {
            orderId: $orderId,
            customerId: c.id,
            customerEmail: c.email,
            productId: p.id,
            quantity: item.quantity,
            price: item.price,
            status: "Pickup Requested",
            orderDate: $orderDate,
            fulfillmentType: "pickup",
            pickupStatus: "requested",
            pickupName: $pickupName,
            pickupPhone: $pickupPhone,
            pickupEmail: $pickupEmail,
            pickupDate: $pickupDate,
            pickupTime: $pickupTime,
            pickupScheduledAt: $pickupScheduledAt,
            pickupNotes: $pickupNotes
          }]->(p)
          SET p.qty_instock = CASE
            WHEN toInteger(p.qty_instock) >= toInteger(item.quantity)
            THEN toString(toInteger(p.qty_instock) - toInteger(item.quantity))
            ELSE p.qty_instock
          END
          RETURN count(p) as createdCount
          `,
          {
            customerId,
            orderId,
            orderDate,
            items: cartItems.map(item => ({
              id: item.id,
              quantity: Number(item.quantity) || 1,
              price: Number(item.price) || 0
            })),
            pickupName: pickupDetails.name || '',
            pickupPhone: pickupDetails.phone || '',
            pickupEmail: pickupDetails.email || '',
            pickupDate,
            pickupTime,
            pickupScheduledAt,
            pickupNotes: pickupDetails.notes || ''
          }
        )
      );

      const createdCountValue = result.records[0]?.get('createdCount');
      const createdCount = typeof createdCountValue?.toNumber === 'function'
        ? createdCountValue.toNumber()
        : Number(createdCountValue || 0);

      if (createdCount === 0) {
        throw new Error('No pickup order rows were created');
      }

      return { orderId, orderDate };
    } catch (error) {
      console.error('Error creating pickup order:', error);
      throw new Error('Failed to create pickup order');
    } finally {
      await session.close();
    }
  },

  async getPickupOrders() {
    const session = driver.session({ database });
    try {
      const result = await session.executeRead(tx =>
        tx.run(
          `
          MATCH (c:Customer)-[o:ORDERED {fulfillmentType: "pickup"}]->(p:Product)
          RETURN {
            orderId: o.orderId,
            customerId: c.id,
            customerName: coalesce(c.name, o.pickupName, "Customer"),
            customerEmail: coalesce(c.email, o.pickupEmail, ""),
            productId: p.id,
            productName: p.name,
            productImage: coalesce(p.main_image, p.image, ""),
            price: coalesce(o.price, p.price),
            quantity: o.quantity,
            status: o.status,
            orderDate: o.orderDate,
            pickupStatus: coalesce(o.pickupStatus, "requested"),
            pickupName: o.pickupName,
            pickupPhone: o.pickupPhone,
            pickupEmail: o.pickupEmail,
            pickupDate: o.pickupDate,
            pickupTime: o.pickupTime,
            pickupScheduledAt: o.pickupScheduledAt,
            pickupNotes: o.pickupNotes,
            stockRestored: coalesce(o.stockRestored, false)
          } as pickupOrder
          ORDER BY coalesce(o.pickupScheduledAt, o.orderDate) ASC, o.orderDate DESC
          `
        )
      );

      return result.records.map(record => record.get('pickupOrder'));
    } catch (error) {
      console.error('Error fetching pickup orders:', error);
      throw new Error('Failed to fetch pickup orders');
    } finally {
      await session.close();
    }
  },

  async updatePickupOrderStatus(orderId, productId, pickupStatus) {
    const session = driver.session({ database });
    const statusMap = {
      requested: 'Pickup Requested',
      ready: 'Ready for Pickup',
      picked_up: 'Picked Up',
      cancelled: 'Cancelled'
    };

    try {
      const result = await session.executeWrite(tx =>
        tx.run(
          `
          MATCH (:Customer)-[o:ORDERED {orderId: $orderId}]->(p:Product)
          WHERE toString(p.id) = toString($productId)
          WITH o, p,
               coalesce(o.pickupStatus, "requested") as previousPickupStatus,
               coalesce(o.stockRestored, false) as stockRestored
          SET p.qty_instock = CASE
                WHEN $pickupStatus = "cancelled" AND stockRestored = false
                THEN toString(toInteger(coalesce(p.qty_instock, 0)) + toInteger(coalesce(o.quantity, 0)))
                WHEN $pickupStatus <> "cancelled" AND stockRestored = true
                THEN toString(
                  CASE
                    WHEN toInteger(coalesce(p.qty_instock, 0)) >= toInteger(coalesce(o.quantity, 0))
                    THEN toInteger(coalesce(p.qty_instock, 0)) - toInteger(coalesce(o.quantity, 0))
                    ELSE toInteger(coalesce(p.qty_instock, 0))
                  END
                )
                ELSE p.qty_instock
              END,
              o.stockRestored = CASE
                WHEN $pickupStatus = "cancelled" AND stockRestored = false
                THEN true
                WHEN $pickupStatus <> "cancelled" AND stockRestored = true
                THEN false
                ELSE coalesce(o.stockRestored, false)
              END,
              o.pickupStatus = $pickupStatus,
              o.status = $status,
              o.updatedAt = datetime()
          RETURN o, p.qty_instock as qtyInStock
          `,
          {
            orderId,
            productId,
            pickupStatus,
            status: statusMap[pickupStatus] || statusMap.requested
          }
        )
      );

      if (result.records.length === 0) {
        throw new Error('Pickup order not found');
      }

      return true;
    } catch (error) {
      console.error('Error updating pickup order status:', error);
      throw new Error('Failed to update pickup order status');
    } finally {
      await session.close();
    }
  },

  async restockCancelledPickupOrder(orderId, productId) {
    const session = driver.session({ database });
    try {
      const result = await session.executeWrite(tx =>
        tx.run(
          `
          MATCH (:Customer)-[o:ORDERED {orderId: $orderId}]->(p:Product)
          WHERE toString(p.id) = toString($productId)
            AND coalesce(o.pickupStatus, "") = "cancelled"
            AND coalesce(o.stockRestored, false) = false
          SET p.qty_instock = toString(toInteger(coalesce(p.qty_instock, 0)) + toInteger(coalesce(o.quantity, 0))),
              o.stockRestored = true,
              o.updatedAt = datetime()
          RETURN o, p.qty_instock as qtyInStock
          `,
          { orderId, productId }
        )
      );

      if (result.records.length === 0) {
        throw new Error('Cancelled pickup order not found or already restocked');
      }

      return true;
    } catch (error) {
      console.error('Error restocking cancelled pickup order:', error);
      throw new Error('Failed to restock cancelled pickup order');
    } finally {
      await session.close();
    }
  },
};

// Driver cleanup is handled in neo4jDriver.js
